import { DateTime } from 'luxon';
import type { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { Tag, Tooltip } from '../../components';
import { Store } from '../../store';
import type { ScheduledEvent } from '../event-details';
import { useCheckinEvent } from '../event-details';
import { CopyPill } from './copy-pill';

interface Props {
  scheduledEvent: ScheduledEvent;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px 18px;
  padding: 15px 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);

  @media (max-width: 520px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Item = styled.div`
  min-width: 0;

  .title {
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.55);
    margin-bottom: 7px;
  }

  .text {
    color: var(--white);
    font-weight: 600;
    font-size: 13px;
  }
`;

const Separator = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: rgba(255, 255, 255, 0.07);
  margin: 1px 0px;
`;

const People = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 8px 10px;
  flex-wrap: wrap;
`;

const Capsule = styled.button<{ $active: boolean; $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px 5px 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  appearance: none;
  font: inherit;
  cursor: ${({ $clickable }): string => ($clickable ? 'pointer' : 'default')};
  transition: border-color var(--transition);

  ${({ $clickable }): string =>
    $clickable ? '&:hover, &:focus { outline: none; border-color: rgba(255, 255, 255, 0.22); }' : ''}

  .circle {
    position: relative;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    flex-shrink: 0;
    overflow: hidden;
    display: grid;
    place-items: center;
    background: ${({ $active }): string =>
      $active ? 'rgba(53, 211, 153, 0.12)' : 'rgba(255, 255, 255, 0.05)'};
    border: 1px solid
      ${({ $active }): string => ($active ? 'rgba(53, 211, 153, 0.55)' : 'rgba(255, 255, 255, 0.1)')};
    color: ${({ $active }): string => ($active ? 'rgb(53, 211, 153)' : 'rgba(176, 186, 209, 0.7)')};
  }

  .circle i {
    font-size: ${({ $active }): string => ($active ? '15px' : '13px')};
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 5px;
    text-align: left;
  }

  .role {
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.45);
    font-weight: 600;
    font-size: 8px;
  }

  .name {
    color: rgba(213, 221, 238, 0.92);
    white-space: nowrap;
    font-weight: 600;
    font-size: 11px;
  }
`;

const Entry = ({ title, children }: { title: string; children: ReactNode }): ReactElement => (
  <Item>
    <div className='title'>{title}</div>
    {children}
  </Item>
);

// Matches the legacy summary's event_type → label mapping.
const eventTypeLabel = (type?: string): string => {
  switch (type) {
    case 'Produced':
      return 'Produced';
    case 'SimLive':
      return 'Simulated Live';
    case 'Mobile':
      return 'Mobile';
    case 'Mobile Kit':
      return 'Mobile Kit';
    case 'Test':
      return 'Test';
    default:
      return 'Static';
  }
};

interface Person {
  role: string;
  name: string;
  icon: string;
  /** Green tick + border when true. */
  active: boolean;
  clickable: boolean;
  onToggle?: () => void;
  title: string;
}

// Active → tick; otherwise the role icon, or a dash when there's no name.
const personIcon = (person: Person): string => {
  if (person.active) {
    return 'far fa-check';
  }

  return person.name ? person.icon : 'far fa-minus';
};

export const EventInfo = ({ scheduledEvent }: Props): ReactElement => {
  /*
   * Times render in the BROWSER's zone, not the venue's: whoever is watching
   * needs to know when this happens for them. No zone label next to it — the
   * whole app renders in the browser zone, and the events toolbar already says
   * which one that is.
   */
  const start = DateTime.fromISO(scheduledEvent.start_time);
  const end = DateTime.fromISO(scheduledEvent.end_time);

  // e.g. "10:40 – 12:20 PM" (meridiem shown once, on the end).
  const timeText = start.isValid ? `${start.toFormat('h:mm')} – ${end.toFormat('h:mm a')}` : '—';

  // Start day only, even when the event runs past midnight into the next one.
  const date = start.isValid ? start.toLocaleString(DateTime.DATE_SHORT) : '—';
  const location = scheduledEvent.location?.name ?? '—';
  const password = scheduledEvent.event_password;

  const { me } = Store.User.useIdentity();
  const [checkinEvent] = useCheckinEvent();

  const videographer = scheduledEvent.assignedOperator?.[0] ?? scheduledEvent.operator_name ?? '';

  // Support toggle = event check-in (the legacy thumbs-up). Active when someone checked in.
  const { checked_user: checkedUser } = scheduledEvent;
  const checkedIn = Boolean(checkedUser);

  const checkedName = checkedUser
    ? `${checkedUser.first_name ?? ''} ${checkedUser.last_name ?? ''}`.trim()
    : '';

  // Once checked in, the capsule shows the returned user; otherwise the support email.
  const supportName = checkedIn && checkedName ? checkedName : scheduledEvent.verifier_email ?? '';

  const toggleCheckin = (): void => {
    void checkinEvent({
      eventId: scheduledEvent.id,
      locationId: scheduledEvent.location?.id,
      checkedUser: !checkedIn && me?.id ? Number(me.id) : undefined
    });
  };

  /*
   * Videographer capsule is read-only: it answers "is one assigned?" and "did they
   * check in?". check_in comes from the operator homepage (operatorInfo), which is
   * the source of truth for the videographer — not operator.confirmation, which
   * only ever existed for operators linked in our own DB.
   */
  const videographerCheckedIn = Boolean(scheduledEvent.operatorInfo?.checked_in);

  let videographerTitle = 'No videographer assigned';

  if (videographer) {
    videographerTitle = videographerCheckedIn ? 'Checked in' : 'Not checked in yet';
  }

  /*
   * TEMP: Audio Mixer capsule hidden for now. When re-enabled it is NOT clickable
   * and shows active only when an audio contact exists:
   *   { role: 'Audio Mixer', name: audioMix, icon: 'far fa-sliders-v',
   *     active: Boolean(scheduledEvent.audioContact), clickable: false, title: '…' }
   */

  const people: Person[] = [
    {
      role: 'Support',
      name: supportName,
      icon: 'far fa-user-headset',
      active: checkedIn,
      clickable: true,
      onToggle: toggleCheckin,
      title: checkedIn ? `Checked in by ${checkedName || 'user'}` : 'Check in'
    },
    {
      role: 'Videographer',
      name: videographer,
      icon: 'far fa-video',
      active: videographerCheckedIn,
      clickable: false,
      title: videographerTitle
    }
  ];

  return (
    <Grid>
      <Entry title='Event Type'>
        <span className='text'>{eventTypeLabel(scheduledEvent.event_type)}</span>
      </Entry>

      <Entry title='Time'>
        <span className='text'>
          {timeText}
        </span>
      </Entry>

      <Entry title='Date'>
        <span className='text'>{date}</span>
      </Entry>

      <Entry title='Location'>
        <span className='text'>{location}</span>
      </Entry>

      <Entry title='Password'>
        {password ? <CopyPill value={password} /> : <span className='text'>—</span>}
      </Entry>

      <Entry title='Status'>
        <Tag className='cc-azure' style={{ border: 'none' }}>
          {scheduledEvent.hidden ? 'Private' : 'Public'}
        </Tag>
      </Entry>

      <Separator />

      <People>
        {people.map((person) => (
          <Tooltip key={person.role} content={person.title} placement='bottom-left'>
            <Capsule
              type='button'
              $active={person.active}
              $clickable={person.clickable}
              onClick={
                person.clickable
                  ? (event): void => {
                      // Drop focus so the tooltip doesn't linger via :focus-within after a click.
                      event.currentTarget.blur();
                      person.onToggle?.();
                    }
                  : undefined
              }
            >
              <span className='circle'>
                <i className={personIcon(person)} />
              </span>
              <span className='info'>
                <span className='role'>{person.role}</span>
                <span className='name'>{person.name || '—'}</span>
              </span>
            </Capsule>
          </Tooltip>
        ))}
      </People>
    </Grid>
  );
};
